import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types'
import styled, { css } from 'styled-components'
import { Link, graphql, StaticQuery } from 'gatsby'
import Img from 'gatsby-image'

import config from '../config'
import { buildNavLink } from '../utils'

const Nav = styled.nav`
  @media (max-width: 860px) {
    transform: scale(0.8);
    transform-origin: left bottom;
  }

  @media (max-width: 540px) {
    transform: scale(0.7);
    transform-origin: left bottom;
  }
`

const List = styled.ul`
  display: flex;
  margin: 0 -12px;
  padding: 0;
  list-style: none;
`

const Item = styled.li`
  a {
    color: inherit;
    text-decoration: none;
  }
`

const Text = styled.span`
  display: block;
  padding: 28px 0;
  position: relative;

  &::after {
    content: '';
    display: block;
    position: absolute;
    bottom: 1px;
    left: -7px;
    right: -7px;
    height: 6px;
    background: transparent;
  }

  @media (max-width: 860px) {
    padding: 0 0 20px 0; /* padding-top comes from the sibling element */
  }
`

const CardImg = styled(Img)`
  display: block !important;
  transition: transform 0.35s ease;
  transform: scale(0.8);
  /* transform: translateZ(0); */
  backface-visibility: hidden;

  img {
    display: block;
  }
`

const Button = styled.div`
  display: block;
  text-decoration: none;
  color: inherit;
  padding: 0 8px;
  position: relative;

  ${props =>
    props.active &&
    css`
      ${CardImg} {
        transform: scale(1);
      }

      ${Text}::after {
        background-color: ${config.colors.blue};
      }
    `}

  &:hover ${CardImg} {
    transform: scale(1);
  }

  @media (max-width: 360px) {
    padding-left: 13px;
    padding-right: 13px;
  }
`

const Compare = styled.div`
  font-size: 18px;
  color: ${config.colors.blue};
  font-family: 'BentonSans';
  font-weight: bold;
  padding-top: 15px;
  padding-bottom: 11px;
`

const CompareDesktop = styled.span`
  @media (max-width: 1024px) {
    display: none;
  }
`

const CompareMobile = styled.span`
  display: none;

  @media (max-width: 1024px) {
    display: inline-block;
  }
`

const query = graphql`
  fragment cardImage on File {
    childImageSharp {
      fixed(width: 80, quality: 100) {
        ...GatsbyImageSharpFixed
      }
    }
  }

  query {
    blueCardImage: file(relativePath: { eq: "card_blue.png" }) {
      ...cardImage
    }
    greenCardImage: file(relativePath: { eq: "card_green.png" }) {
      ...cardImage
    }
    goldCardImage: file(relativePath: { eq: "card_gold.png" }) {
      ...cardImage
    }
    platinumCardImage: file(relativePath: { eq: "card_platinum.png" }) {
      ...cardImage
    }
  }
`

const CardNav = ({
  currentCard,
  disableBlue,
  disableGreen,
  cardLinks,
  currentVariant,
  disableCompare,
}) => {

  const [platinumLink, setPlatinumLink] = useState('/platinum')
  useEffect(() => {
    let platinumHref = 'https://amex-card.at/platinum'

    if (window.location.pathname.includes('-aff')) {
      platinumHref += '-aff'
    }
    platinumHref += '/'
    setPlatinumLink(platinumHref)
  }, [])

  return (
    <StaticQuery
      query={query}
      render={data => (
        <Nav>
          <List>
            {!disableBlue && (
              <Item>
                <Link
                  to={
                    cardLinks && cardLinks.blue
                      ? buildNavLink(
                        currentVariant,
                        'blue',
                        cardLinks.blue
                      )
                      : buildNavLink(
                        currentVariant,
                        'blue',
                        '/blue'
                      )
                  }>
                  <Button active={currentCard === 'blue'}>
                    <Text>
                      <CardImg
                        fixed={data.blueCardImage.childImageSharp.fixed}
                      />
                    </Text>
                  </Button>
                </Link>
              </Item>
            )}
            {!disableGreen && (
              <Item>
                <Link
                  to={
                    cardLinks && cardLinks.green
                      ? buildNavLink(
                        currentVariant,
                        'green',
                        cardLinks.green
                      )
                      : buildNavLink(
                        currentVariant,
                        'green',
                        '/green'
                      )
                  }>
                  <Button active={currentCard === 'green'}>
                    <Text>
                      <CardImg
                        fixed={data.greenCardImage.childImageSharp.fixed}
                      />
                    </Text>
                  </Button>
                </Link>
              </Item>
            )}
            <Item>
              <Link
                to={
                  cardLinks && cardLinks.gold
                    ? buildNavLink(
                      currentVariant,
                      'gold',
                      cardLinks.gold
                    )
                    : buildNavLink(
                      currentVariant,
                      'gold',
                      '/gold'
                    )
                }>
                <Button active={currentCard === 'gold'}>
                  <Text>
                    <CardImg fixed={data.goldCardImage.childImageSharp.fixed} />
                  </Text>
                </Button>
              </Link>
            </Item>
            <Item>
              <a href={platinumLink}>
                <Button active={currentCard === 'platinum'}>
                  <Text>
                    <CardImg
                      fixed={data.platinumCardImage.childImageSharp.fixed}
                    />
                  </Text>
                </Button>
              </a>
            </Item>
            {!disableBlue && !disableCompare && (
              <Item>
                <Link to={buildNavLink(null, null, '/angebot-vergleichen')}>
                  <Button active={currentCard === 'multi'}>
                    <Text>
                      <Compare>
                        <CompareDesktop>Karten vergleichen</CompareDesktop>
                        <CompareMobile>Alle</CompareMobile>
                      </Compare>
                    </Text>
                  </Button>
                </Link>
              </Item>
            )}
          </List>
        </Nav>
      )}
    />
  )
}

CardNav.propTypes = {
  currentCard: PropTypes.string,
}

export default CardNav
